import {CommonModule} from '@angular/common';
import {Component, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faSquarePlus} from '@fortawesome/free-solid-svg-icons';
import {ToastrService} from 'ngx-toastr';
import {Observable, of, Subscription, throwError} from 'rxjs';
import {catchError, concatMap, map, switchMap} from 'rxjs/operators';
import {AuthService} from '../../../services/auth.service';
import {
    EenCustomerAccountApiService
} from '../../../services/vision-provider-management/een-customer-account-api.service';
import {
    EenCustomerAccountListItem,
    EenCustomerAccountOrganizationAssignmentResponse,
} from '../../../models/vision-provider-management';

/** Select value for an unassigned organization. */
export const EEN_ACCOUNT_NONE_VALUE = '';

/** Which step of a non-atomic A→B remapping failed. */
export type EenRemapFailureStage = 'remove' | 'add';

const REMAP_STAGE_KEY = 'eenRemapFailureStage';

function annotateRemapStage(error: unknown, stage: EenRemapFailureStage): unknown {
    if (error !== null && typeof error === 'object') {
        (error as Record<string, unknown>)[REMAP_STAGE_KEY] = stage;
        return error;
    }
    return {[REMAP_STAGE_KEY]: stage, error: {detail: String(error)}};
}

function readRemapStage(error: unknown): EenRemapFailureStage | null {
    if (error === null || typeof error !== 'object') {
        return null;
    }
    const stage = (error as Record<string, unknown>)[REMAP_STAGE_KEY];
    return stage === 'remove' || stage === 'add' ? stage : null;
}

@Component({
    selector: 'app-organization-een-customer-account',
    standalone: true,
    imports: [CommonModule, FormsModule, FontAwesomeModule, RouterLink],
    templateUrl: './organization-een-customer-account.component.html',
    styleUrl: './organization-een-customer-account.component.css',
})
export class OrganizationEenCustomerAccountComponent implements OnChanges, OnDestroy {
    @Input({required: true}) organizationId!: number | string | null;
    @Input() organizationName: string | null = null;
    /** When embedded under Organization tabs, hide the redundant panel title. */
    @Input() hidePanelHeading = false;
    /**
     * Operational Readiness: display the persisted assignment without edit controls.
     * Assignment/reassignment remains on the dedicated EEN Account tab.
     */
    @Input() readOnly = false;

    readonly noneValue = EEN_ACCOUNT_NONE_VALUE;

    loading = false;
    saving = false;
    loadError: string | null = null;
    /** Shown when the org's mapped account is inactive/stale and not assignable. */
    staleMappingWarning: string | null = null;
    environment = '';
    accounts: EenCustomerAccountListItem[] = [];
    private listedAccounts: EenCustomerAccountListItem[] = [];
    currentEenAccountId: string | null = null;
    currentEenAccountName: string | null = null;
    selectedEenAccountId: string = EEN_ACCOUNT_NONE_VALUE;

    private loadSub: Subscription | null = null;
    private saveSub: Subscription | null = null;

    constructor(
        private api: EenCustomerAccountApiService,
        private authService: AuthService,
        private toastr: ToastrService,
        library: FaIconLibrary,
    ) {
        library.addIcons(faSquarePlus);
    }

    get panelVisible(): boolean {
        return this.authService.isSuperUser() && !!this.resolvedOrganizationId;
    }

    get resolvedOrganizationId(): number | null {
        const raw = this.organizationId;
        if (raw === null || raw === undefined || raw === '') {
            return null;
        }
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
    }

    get hasChanges(): boolean {
        return this.normalizeAccountId(this.selectedEenAccountId)
            !== this.normalizeAccountId(this.currentEenAccountId);
    }

    get saveDisabled(): boolean {
        if (this.readOnly || this.loading || this.saving || !!this.loadError || !this.hasChanges) {
            return true;
        }
        const target = this.normalizeAccountId(this.selectedEenAccountId);
        if (!target) {
            return false;
        }
        return !this.accounts.some(
            (account) => account.een_account_id === target && this.isAssignableAccount(account),
        );
    }

    /** Assigned account row for read-only display (never invents credentials). */
    get assignedAccount(): EenCustomerAccountListItem | null {
        const mappedId = this.normalizeAccountId(this.currentEenAccountId);
        if (!mappedId) {
            return null;
        }
        return (
            this.accounts.find((account) => account.een_account_id === mappedId)
            || this.listedAccounts.find((account) => account.een_account_id === mappedId)
            || null
        );
    }

    get assignedAccountLabel(): string {
        const account = this.assignedAccount;
        if (account) {
            return this.accountLabel(account);
        }
        if (this.currentEenAccountId) {
            const name = (this.currentEenAccountName || '').trim();
            return name
                ? `${name} (${this.currentEenAccountId})`
                : this.currentEenAccountId;
        }
        return '';
    }

    get assignedAccountStatusLabel(): string {
        const status = (this.assignedAccount?.status || '').trim();
        return status || 'unknown';
    }

    get assignedAuthorizationStatusLabel(): string | null {
        const status = (this.assignedAccount?.authorization?.status || '').trim();
        return status || null;
    }

    get eenAccountTabLink(): string[] | null {
        const orgId = this.resolvedOrganizationId;
        if (!orgId) {
            return null;
        }
        return ['/organization', String(orgId), 'een-account'];
    }

    get hasRegisteredAccounts(): boolean {
        return this.listedAccounts.length > 0;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['organizationId'] || changes['readOnly']) && this.panelVisible) {
            this.loadPanelState();
        }
    }

    ngOnDestroy(): void {
        this.cancelInFlightWork();
    }

    accountLabel(account: EenCustomerAccountListItem): string {
        return `${account.name} (${account.een_account_id})`;
    }

    formatEnvironmentLabel(value: string): string {
        return (value || '').trim().toUpperCase() || 'UNKNOWN';
    }

    onSave(): void {
        if (this.readOnly || this.saveDisabled || !this.resolvedOrganizationId) {
            return;
        }

        const orgId = this.resolvedOrganizationId;
        const targetAccountId = this.normalizeAccountId(this.selectedEenAccountId);
        const currentAccountId = this.normalizeAccountId(this.currentEenAccountId);

        if (targetAccountId === currentAccountId) {
            return;
        }

        const wasRemap = !!currentAccountId && !!targetAccountId && currentAccountId !== targetAccountId;

        this.saveSub?.unsubscribe();
        this.saving = true;
        this.saveSub = this.buildSaveOperation(currentAccountId, targetAccountId, orgId).subscribe({
            next: () => {
                this.saving = false;
                this.toastr.success('EEN customer account mapping saved.', 'Success');
                this.loadPanelState();
            },
            error: (error) => {
                this.saving = false;
                this.handleAssignmentError(error, {wasRemap});
                this.loadPanelState({afterSaveFailure: true});
            },
        });
    }

    /** Exposed for focused unit tests of replace-all assignment semantics. */
    buildSaveOperation(
        currentAccountId: string | null,
        targetAccountId: string | null,
        organizationId: number,
    ): Observable<void> {
        if (!currentAccountId && targetAccountId) {
            return this.addOrganizationToAccount(targetAccountId, organizationId);
        }
        if (currentAccountId && !targetAccountId) {
            return this.removeOrganizationFromAccount(currentAccountId, organizationId);
        }
        if (currentAccountId && targetAccountId && currentAccountId !== targetAccountId) {
            // Non-atomic remove-then-add: annotate which stage failed for honest messaging.
            return this.removeOrganizationFromAccount(currentAccountId, organizationId).pipe(
                catchError((error) => throwError(() => annotateRemapStage(error, 'remove'))),
                concatMap(() =>
                    this.addOrganizationToAccount(targetAccountId, organizationId).pipe(
                        catchError((error) => throwError(() => annotateRemapStage(error, 'add'))),
                    ),
                ),
            );
        }
        return of(undefined);
    }

    private loadPanelState(options: {afterSaveFailure?: boolean} = {}): void {
        const orgId = this.resolvedOrganizationId;
        if (!orgId) {
            return;
        }

        this.cancelInFlightWork();
        this.loading = true;
        this.loadError = null;
        this.staleMappingWarning = null;
        this.accounts = [];
        this.listedAccounts = [];
        this.currentEenAccountId = null;
        this.currentEenAccountName = null;
        this.selectedEenAccountId = EEN_ACCOUNT_NONE_VALUE;

        // switchMap cancels in-flight assignment GETs when organizationId changes.
        this.loadSub = this.api.list().pipe(
            switchMap((listResponse) => {
                this.environment = listResponse.environment || '';
                // Backend lists end-user accounts only; offer active/assignable ones
                // for selection. Keep a non-assignable current mapping visible via apply.
                this.listedAccounts = listResponse.results || [];
                this.accounts = this.listedAccounts.filter((account) =>
                    this.isAssignableAccount(account),
                );

                if (this.listedAccounts.length === 0) {
                    return of({kind: 'empty' as const});
                }

                // Assignment GET is account-scoped for the `assigned` flag, but the
                // backend returns every KYRA organization with that org's authoritative
                // `current_een_account_id` (FK), regardless of which account is probed.
                const probeAccountId = this.listedAccounts[0].een_account_id;
                return this.api.getOrganizationAssignments(probeAccountId).pipe(
                    map((assignmentResponse) => ({
                        kind: 'mapped' as const,
                        orgId,
                        assignmentResponse,
                    })),
                    catchError((error) => {
                        this.loadError = this.extractErrorDetail(
                            error,
                            'Unable to load EEN customer account mapping.',
                        );
                        return of({kind: 'error' as const});
                    }),
                );
            }),
            catchError((error) => {
                this.loadError = this.extractErrorDetail(
                    error,
                    'Unable to load EEN customer accounts.',
                );
                return of({kind: 'error' as const});
            }),
        ).subscribe({
            next: (result) => {
                if (result.kind === 'mapped') {
                    this.applyCurrentMapping(result.orgId, result.assignmentResponse);
                } else if (result.kind === 'error' && options.afterSaveFailure) {
                    this.toastr.error(
                        'Could not reload the current EEN account mapping after the save failure. Refresh the page to see the server state.',
                        'Recovery failed',
                    );
                }
                this.loading = false;
            },
        });
    }

    private cancelInFlightWork(): void {
        this.loadSub?.unsubscribe();
        this.loadSub = null;
        this.saveSub?.unsubscribe();
        this.saveSub = null;
        this.saving = false;
    }

    private applyCurrentMapping(
        orgId: number,
        assignmentResponse: EenCustomerAccountOrganizationAssignmentResponse,
    ): void {
        const orgRow = (assignmentResponse.organizations || []).find((row) => row.id === orgId);
        const mappedId = orgRow?.current_een_account_id || null;
        this.currentEenAccountId = mappedId;
        this.currentEenAccountName = orgRow?.current_een_account_name || null;
        this.selectedEenAccountId = mappedId || EEN_ACCOUNT_NONE_VALUE;
        this.staleMappingWarning = null;

        if (!mappedId) {
            return;
        }
        const alreadyListed = this.accounts.some((account) => account.een_account_id === mappedId);
        if (alreadyListed) {
            return;
        }
        const listedRow = this.listedAccounts.find((account) => account.een_account_id === mappedId);
        if (listedRow) {
            this.accounts = [listedRow, ...this.accounts];
            this.staleMappingWarning = this.readOnly
                ? 'The assigned EEN account is not currently assignable (inactive or not an '
                    + 'active end-user account). Update the assignment on the EEN Account tab.'
                : 'The mapped EEN account is not currently assignable (inactive or not an '
                    + 'active end-user account). Select an active account or clear the mapping.';
            return;
        }
        // Stale FK not in list (e.g. type filtered out) — still show a label for honesty.
        this.accounts = [
            {
                een_account_id: mappedId,
                name: orgRow?.current_een_account_name || mappedId,
                account_type: 'endUserAccount',
                status: 'unknown',
                parent_id: null,
                assignable: false,
                authorization: null,
            },
            ...this.accounts,
        ];
        this.staleMappingWarning = this.readOnly
            ? 'The assigned EEN account is missing from the current environment account list. '
                + 'Update the assignment on the EEN Account tab.'
            : 'The mapped EEN account is missing from the current selectable list. '
                + 'Clear the mapping or choose an active end-user account.';
    }

    private addOrganizationToAccount(
        eenAccountId: string,
        organizationId: number,
    ): Observable<void> {
        return this.api.getOrganizationAssignments(eenAccountId).pipe(
            concatMap((response) => {
                const organizationIds = this.assignedOrganizationIds(response);
                if (!organizationIds.includes(organizationId)) {
                    organizationIds.push(organizationId);
                }
                return this.api.saveOrganizationAssignments(eenAccountId, {organization_ids: organizationIds});
            }),
            concatMap(() => of(undefined)),
        );
    }

    private removeOrganizationFromAccount(
        eenAccountId: string,
        organizationId: number,
    ): Observable<void> {
        return this.api.getOrganizationAssignments(eenAccountId).pipe(
            concatMap((response) => {
                const organizationIds = this.assignedOrganizationIds(response)
                    .filter((id) => id !== organizationId);
                return this.api.saveOrganizationAssignments(eenAccountId, {organization_ids: organizationIds});
            }),
            concatMap(() => of(undefined)),
        );
    }

    private assignedOrganizationIds(
        response: EenCustomerAccountOrganizationAssignmentResponse,
    ): number[] {
        return (response.organizations || [])
            .filter((row) => row.assigned)
            .map((row) => row.id);
    }

    private normalizeAccountId(value: string | null | undefined): string | null {
        if (value === null || value === undefined || value === EEN_ACCOUNT_NONE_VALUE) {
            return null;
        }
        return value;
    }

    /** Active end-user accounts only; backend also enforces on PUT. */
    private isAssignableAccount(account: EenCustomerAccountListItem): boolean {
        if (account.assignable === true) {
            return true;
        }
        if (account.assignable === false) {
            return false;
        }
        // Backward-compatible fallback if an older API omits the flag.
        return (
            account.account_type === 'endUserAccount'
            && (account.status || '').trim() === 'active'
        );
    }

    private handleAssignmentError(
        error: unknown,
        options: {wasRemap?: boolean} = {},
    ): void {
        const body = (error as {error?: Record<string, unknown>})?.error;
        const code = typeof body?.['error_code'] === 'string' ? body['error_code'] : null;
        let detail = this.extractErrorDetail(error, 'Unable to save EEN customer account mapping.');
        const requiresReauth = body?.['requires_reauthorization'] === true;
        const remapStage = readRemapStage(error);

        if (options.wasRemap) {
            if (remapStage === 'remove') {
                detail = `${detail} The account move did not start; the previous assignment was not removed. Reloading the server mapping.`;
            } else if (remapStage === 'add') {
                detail = `${detail} The remapping did not complete after the previous assignment was removed; the organization may now be unassigned. Reloading the server mapping.`;
            } else {
                detail = `${detail} The remapping did not complete. Reloading the server mapping.`;
            }
        }

        if (code === 'een_account_not_authorized' || code === 'een_account_needs_reauth' || requiresReauth) {
            this.toastr.error(
                `${detail} Connect or reconnect the EEN account from the Authentication page.`,
                'Authorization required',
            );
            return;
        }

        if (
            (error as {status?: number})?.status === 409
            || code === 'organization_assignment_conflict'
        ) {
            this.toastr.error(detail, 'Conflict');
            return;
        }

        this.toastr.error(detail, 'Error');
    }

    private extractErrorDetail(error: unknown, fallback: string): string {
        const body = (error as {error?: {detail?: unknown}})?.error;
        if (typeof body?.detail === 'string' && body.detail.trim()) {
            return body.detail;
        }
        return fallback;
    }
}
